import { Component, OnInit } from '@angular/core'
import { BackendService } from 'src/app/services/backend.service'
import { AdmytService } from 'src/app/services/admyt.service'
import { environment } from 'src/environments/environment'
import Swal from 'sweetalert2'

interface TestRoute {
    name: string
    endpoint: string
    description: string
    category: string
    schedule?: string
    frequency?: string
    isRunning?: boolean
    hasTestCallable: boolean
    /**
     * When true, the Run button is also exposed on production (not just on the emulator).
     * Use only for idempotent one-off operations like seeds, backfills, and one-time migrations.
     * Default (omitted/false) is emulator-only, matching the original safety stance for cron tests.
     */
    runOnProduction?: boolean
}

@Component({
    selector: 'app-admin-test-routes',
    templateUrl: './admin-test-routes.component.html',
    styleUrls: ['./admin-test-routes.component.scss'],
})
export class AdminTestRoutesComponent implements OnInit {
    isEmulator = environment.useEmulators

    testRoutes: TestRoute[] = [
        {
            name: 'Refresh Google Reviews',
            endpoint: 'refreshGoogleReviews',
            description: 'Fetch the latest 50 reviews and the full-profile rating/count directly from ParkUpp’s Google Business Profile. Requires Google API approval and OAuth setup. A failed refresh preserves the last usable snapshot.',
            category: 'Data Sync',
            schedule: 'Every 4 days; checked hourly (Africa/Johannesburg), with hourly retry on failure',
            frequency: 'Every 4 days',
            hasTestCallable: true,
            runOnProduction: true,
        },
        {
            name: 'Affiliate Monthly Statements',
            endpoint: 'affiliateAdminGenerateStatements',
            description: 'Close last month and create one fee share statement per affiliate, payable by the 25th (reverses commissions whose invoice is no longer paid). Manual only, never scheduled; also available on Accounts → Affiliate Payouts with a month picker. Safe to re-run: existing statements are not duplicated.',
            category: 'Affiliates',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
        },
        {
            name: 'Backfill Client Attribution (dry run)',
            endpoint: 'backfillClientAttribution-dry',
            description: 'Count clients, subscriptions and invoices missing p_source/p_campaign that can be restored from clientsAdmin (attribution wiped by the old profile form). Changes nothing.',
            category: 'Data Sync',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
        },
        {
            name: 'Backfill Client Attribution (apply)',
            endpoint: 'backfillClientAttribution-apply',
            description: 'Restore p_source/p_campaign from clientsAdmin onto clients, and onto their subscriptions and invoices that have none. Never overwrites existing values.',
            category: 'Data Sync',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
        },
        { name: 'Refresh Parking Insights', endpoint: 'refreshParkingInsights', description: 'Rebuild public Market Insights and private landlord snapshots from current data and confirmed building allocations. Runs automatically at 06:30 SAST.', category: 'Data Sync', schedule: 'Daily at 06:30 (Africa/Johannesburg)', frequency: 'Daily', hasTestCallable: true },
        // CRM
        {
            name: 'Full Subscription Data Sync',
            endpoint: 'syncAdminClientsSubscriptionData',
            description: 'Rebuild subscriptionSummaries and CRM fields for all admin clients from source of truth (daily safety net + one-off migration)',
            category: 'CRM',
            schedule: 'Every day at 04:00',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'Close Old Leads (Pre-2026)',
            endpoint: 'migrateCloseOldLeads',
            description: 'Close all CRM leads created before 2026 that are still open. Sets status to Closed with reason "Manually Closed - Old Lead".',
            category: 'CRM',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
            runOnProduction: true,
        },
        {
            name: 'Delete CRM Users',
            endpoint: 'deleteCrmUsers',
            description: 'Temporary bot purge. Permanently deletes Auth + clients + clientsAdmin for the given UIDs. Skips users with subscriptions. Prefer the Users view multi-select in Parker CRM.',
            category: 'CRM',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
            runOnProduction: true,
        },
        {
            name: 'Gravatar Avatar Sync',
            endpoint: 'testSyncGravatarAvatars',
            description: 'Fetch Gravatar profile images for CRM leads using their email addresses (500 per run, 1000/hour rate limit)',
            category: 'CRM',
            schedule: 'Every hour',
            frequency: 'Hourly',
            hasTestCallable: true,
        },
        {
            name: 'Google Ads Conversion Upload',
            endpoint: 'testUploadGoogleAdsConversions',
            description: 'Upload pending offline conversions to Google Ads via the Data Manager API. Runs with validateOnly=true by default when triggered manually.',
            category: 'CRM',
            schedule: 'Every hour',
            frequency: 'Hourly',
            hasTestCallable: true,
        },

        {
            name: 'Google Ads Missing Conversion Scan',
            endpoint: 'reconcileGoogleAdsConversions',
            description: 'Preview the first 200 subscriptions for missed activations. The hourly job automatically resumes the scan and queues eligible missing conversions.',
            category: 'CRM', schedule: 'Every hour (resumable)', frequency: 'Hourly', hasTestCallable: true,
        },
        {
            name: 'Google Ads Processing Verification',
            endpoint: 'verifyGoogleAdsConversions',
            description: 'Retrieve final Google processing status and batch warnings/errors for accepted uploads.',
            category: 'CRM', schedule: 'Every hour', frequency: 'Hourly', hasTestCallable: true,
        },

        // Email Reports
        {
            name: 'Merchant Rejections Report',
            endpoint: 'testRoutes-testMerchantRejectionsReport',
            description: 'Send weekly merchant rejections email report',
            category: 'Email Reports',
            schedule: 'Every Friday at 07:10 (Africa/Johannesburg)',
            frequency: 'Weekly',
            hasTestCallable: true,
        },
        {
            name: 'ParkUpp Daily Expiring Contracts Report',
            endpoint: 'testRoutes-testSubscriptionExpirationReport',
            description: 'Send daily expiring contracts email report (subscriptions expiring in 1 or 5 days)',
            category: 'Email Reports',
            schedule: 'Every day at 07:00 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'ParkUpp Daily Email Leads',
            endpoint: 'testRoutes-testDailyEmailLeadsReport',
            description: 'Send daily email leads report (new users without subscriptions in last 7 days)',
            category: 'Email Reports',
            schedule: 'Every day at 07:05 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'ParkUpp Daily Sales Bell',
            endpoint: 'testRoutes-testDailySalesBellReport',
            description: "Send daily sales bell email summarising yesterday's new subscriptions, grouped by enterprise (portfolio) and merchant, with top-line stats (bays, MRR, contract value, new leads, new parkings, total active subs)",
            category: 'Email Reports',
            schedule: 'Every day at 07:00 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
            runOnProduction: true,
        },
        {
            name: 'Parker Aging Invoices Report',
            endpoint: 'testRoutes-testDailyAgingInvoicesReport',
            description: 'Send daily aging invoices report showing unpaid parker invoices grouped by client with aging buckets',
            category: 'Email Reports',
            schedule: 'Every day at 07:15 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'WhatsApp No Parking Users',
            endpoint: 'testWhatsappNoParkingUsers',
            description: 'Send WhatsApp messages to users who signed up yesterday without subscriptions',
            category: 'Email Reports',
            schedule: 'Every day at 10:00',
            frequency: 'Daily',
            hasTestCallable: true,
        },

        {
            name: 'Merchant Monthly Report',
            endpoint: 'testSendMerchantMonthlyReport',
            description: 'Queue monthly PDF report jobs for all merchants with active subscriptions (one background job per merchant). Pass a merchantKey to queue a single report. Emails arrive asynchronously.',
            category: 'Email Reports',
            schedule: '1st of month at 08:00 (Africa/Johannesburg)',
            frequency: 'Monthly',
            hasTestCallable: true,
        },
        {
            name: 'Merchant Monthly Report — Preview',
            endpoint: 'testPreviewMerchantMonthlyReport',
            description: 'Queue preview report jobs for all merchants. Emails go to the ParkUpp internal team (mark, lesedi, jett, accounts) instead of actual merchants. Emails arrive asynchronously. Safe to run anytime.',
            category: 'Email Reports',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
            runOnProduction: true,
        },

        // Invoices
        {
            name: 'Generate Invoices',
            endpoint: 'testCronGenerateInvoices',
            description: 'Generate invoices for active subscriptions',
            category: 'Invoices',
            schedule: 'Every day at 06:00 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'Process Invoice Payments',
            endpoint: 'testCronProcessInvoicePayments',
            description: 'Process all pending invoice payments',
            category: 'Invoices',
            schedule: 'Every day at 17:10 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'Check Due Invoices',
            endpoint: 'testCronCheckDueInvoices',
            description: 'Check and process overdue invoices',
            category: 'Invoices',
            schedule: 'Every day at 07:00 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'Send M2M Renewal Reminders',
            endpoint: 'testCronSendM2MRenewalReminders',
            description: 'Send renewal reminder emails to M2M subscribers 14 days before renewal (7-day cancellation window)',
            category: 'Invoices',
            schedule: 'Every day at 05:55 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'Update Merchant Response Time',
            endpoint: 'testCronUpdateMerchantResponseTime',
            description: 'Calculate and update merchant average response times',
            category: 'Invoices',
            schedule: 'Every day at 08:00 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },

        // Subscriptions
        {
            name: 'Subscription Ending Reminder',
            endpoint: 'testRoutes-testSubscriptionEndingReminder',
            description: 'Send renewal reminders for subscriptions ending in 1-12 days',
            category: 'Subscriptions',
            schedule: 'Every day at 11:30 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'Expire Old Subscriptions',
            endpoint: 'schedulers-testCronSubscriptionExpired',
            description: 'Find and expire subscriptions with paymentInitiated status older than 24 hours',
            category: 'Subscriptions',
            schedule: 'Every hour',
            frequency: 'Hourly',
            hasTestCallable: true,
        },
        {
            name: 'Retry First Payments',
            endpoint: 'schedulers-testCronRetryFirstPayments',
            description: 'Retry accepted requests whose first payment hit a PayGate problem (firstPaymentRetryAt due). Escalates to admins after the third attempt',
            category: 'Subscriptions',
            schedule: 'Every 5 minutes',
            frequency: 'Every 5 minutes',
            hasTestCallable: true,
        },
        {
            name: 'Find Inactive Subscriptions',
            endpoint: 'cronFindInactiveSubscriptions',
            description: 'Find subscriptions with status "new" older than 24 hours and mark them cancelled',
            category: 'Subscriptions',
            schedule: 'Every hour',
            frequency: 'Hourly',
            hasTestCallable: false,
        },
        {
            name: 'Subscription Getting Old',
            endpoint: 'cronSubscriptionGettingOld',
            description: 'Send merchant reminders for paymentInitiated subscriptions older than 10 hours',
            category: 'Subscriptions',
            schedule: 'Every hour',
            frequency: 'Hourly',
            hasTestCallable: false,
        },
        {
            name: 'Find Completed Subscriptions',
            endpoint: 'cronFindCompletedSubscriptions',
            description: 'Find active subscriptions past their end date and mark them as completed',
            category: 'Subscriptions',
            schedule: 'Every day at 04:00 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: false,
        },

        // Data Sync
        {
            name: 'Clone Clients to Admin Clients',
            endpoint: 'testCloneClientsToAdminClients',
            description: 'Sync client data to admin clients table',
            category: 'Data Sync',
            schedule: 'Manual only (replaced by real-time listeners)',
            frequency: 'Manual',
            hasTestCallable: true,
        },
        {
            name: 'Clone Merchants to Private',
            endpoint: 'testCloneMerchantsToMerchantsPrivate',
            description: 'Sync merchant data to private merchants table',
            category: 'Data Sync',
            schedule: 'Every day at 05:00',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'Save Dashboard Data',
            endpoint: 'testSaveDashboardData',
            description: 'Aggregate and save dashboard statistics',
            category: 'Data Sync',
            schedule: 'Every day at 23:30',
            frequency: 'Daily',
            hasTestCallable: true,
        },
        {
            name: 'Aggregate Daily Tracking Data',
            endpoint: 'aggregateDailyTrackingData',
            description: 'Aggregate daily tracking data for analytics',
            category: 'Data Sync',
            schedule: 'Every day at 03:00 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: false,
        },
        {
            name: 'Aggregate Monthly Tracking Data',
            endpoint: 'aggregateMonthlyTrackingData',
            description: 'Aggregate monthly tracking data for analytics',
            category: 'Data Sync',
            schedule: '1st of month at 03:00 (Africa/Johannesburg)',
            frequency: 'Monthly',
            hasTestCallable: false,
        },
        {
            name: 'Backfill Parking Reports',
            endpoint: 'backfillParkingReports',
            description: 'Clears and rebuilds dailyParkingReports and monthlyParkingReports from all trackingEntries and subscriptions (SAST days; bookings = subscriptions that went live). Use after changing the aggregation logic or for initial data population.',
            category: 'Data Sync',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
        },

        // Admyt
        {
            name: 'Re-sync Client Admyt Integration',
            endpoint: 'resyncClientAdmyt',
            description: 'Re-sync a client\'s Admyt access control integration. Creates/verifies Admyt tenants and tenant users for all active subscriptions on Admyt parkings. Requires a client ID.',
            category: 'Admyt',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
            runOnProduction: true,
        },
        {
            name: 'Reconcile Admyt Access Flags',
            endpoint: 'reconcileAdmytAccess',
            description: 'Finds all clients with active subscriptions on Admyt-linked parkings and ensures hasAdmytAccess is set to true. Daily safety net for the real-time listener.',
            category: 'Admyt',
            schedule: 'Every day at 04:30 (Africa/Johannesburg)',
            frequency: 'Daily',
            hasTestCallable: true,
        },

        // Omnisend
        {
            name: 'Sync All Admin Clients to Omnisend',
            endpoint: 'testSyncAllAdminClientsToOmnisend',
            description: 'One-time backfill: creates or updates Omnisend contacts for all admin clients with email addresses. Syncs enterprise, location, bays, campaign data, and CRM fields.',
            category: 'Omnisend',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
            runOnProduction: true,
        },
        {
            name: 'Seed Omnisend Product Categories',
            endpoint: 'seedOmnisendProductCategories',
            description: 'Creates / updates the parking-type product categories in Omnisend (Monthly, Collector, Events, Flex). Idempotent: safe to re-run. Closes the "Product Categories: Data not syncing" alert in Omnisend Health.',
            category: 'Omnisend',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
            runOnProduction: true,
        },

        // Migrations
        {
            name: 'Migrate Occupants to Parkers',
            endpoint: 'migrateOccupantsToParkers',
            description: 'One-time migration: creates a default Parker per client, copies vehicles from occupants to parkers/vehicles, and updates subscription parkerRefs. Safe to re-run (idempotent).',
            category: 'Migrations',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
            runOnProduction: true,
        },

        // Prospecting
        {
            name: 'Poll Prospect Scrapes',
            endpoint: 'prospecting-testCronPollProspectScrapes',
            description: 'Poll running Apify Google Maps scrape runs, import completed datasets into prospects (dedup by placeId then domain, with geoHash + nearest parkings), and mark failures.',
            category: 'Prospecting',
            schedule: 'Every 5 minutes',
            frequency: 'Hourly',
            hasTestCallable: true,
            runOnProduction: true,
        },

        // CMS
        {
            name: 'Seed FAQs',
            endpoint: 'seedFaqs',
            description:
                'Populate the faqs Firestore collection with the original hardcoded FAQs (Driver + Property Owner). Idempotent: re-running updates question/answer text without duplicating, and preserves any admin edits to order/published.',
            category: 'CMS',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
            runOnProduction: true,
        },

        // Data Repair
        {
            name: 'Repair EventLog Timestamps',
            endpoint: 'repairEventLogTimestamps',
            description:
                'Repairs corrupted eventLog timestamps on subscriptions. Converts plain { seconds, nanoseconds } objects back into native Firestore Timestamps. Safe to re-run. Pass subscriptionKey to target a single subscription.',
            category: 'Data Repair',
            schedule: 'Manual only',
            frequency: 'Manual',
            hasTestCallable: true,
            runOnProduction: true,
        },
    ]

    categories: string[] = []
    selectedCategory: string = 'All'

    constructor(private backendService: BackendService, private admytService: AdmytService) {}

    ngOnInit(): void {
        // Extract unique categories
        const categorySet = new Set(this.testRoutes.map((route) => route.category))
        this.categories = ['All', ...Array.from(categorySet).sort()]
    }

    private frequencyOrder: Record<string, number> = {
        Hourly: 0,
        Daily: 1,
        Weekly: 2,
        Monthly: 3,
        Manual: 4,
    }

    get filteredRoutes(): TestRoute[] {
        const routes = this.selectedCategory === 'All' ? this.testRoutes : this.testRoutes.filter((route) => route.category === this.selectedCategory)
        return [...routes].sort((a, b) => {
            const aOrder = this.frequencyOrder[a.frequency || 'Manual'] ?? 5
            const bOrder = this.frequencyOrder[b.frequency || 'Manual'] ?? 5
            return aOrder - bOrder
        })
    }

    async runTest(route: TestRoute) {
        if (route.isRunning) return

        const confirmResult = await Swal.fire({
            title: `Run ${route.name}?`,
            text: route.description,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, run it',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        route.isRunning = true

        try {
            let response: any

            // Call the appropriate backend service method based on endpoint
            switch (route.endpoint) {
                case 'testRoutes-testSubscriptionExpirationReport':
                    response = await this.backendService.testSubscriptionExpirationReport()
                    break
                case 'testRoutes-testMerchantRejectionsReport':
                    response = await this.backendService.testMerchantRejectionsReport()
                    break
                case 'testRoutes-testDailyEmailLeadsReport':
                    response = await this.backendService.testDailyEmailLeadsReport()
                    break
                case 'testRoutes-testDailySalesBellReport':
                    response = await this.backendService.testDailySalesBellReport()
                    break
                case 'testRoutes-testDailyAgingInvoicesReport':
                    response = await this.backendService.testDailyAgingInvoicesReport()
                    break
                case 'testRoutes-testSubscriptionEndingReminder':
                    response = await this.backendService.testSubscriptionEndingReminder()
                    break
                case 'testWhatsappNoParkingUsers':
                    response = await this.backendService.testWhatsappNoParkingUsers()
                    break
                case 'testCronGenerateInvoices':
                    response = await this.backendService.testCronGenerateInvoices()
                    break
                case 'testCronProcessInvoicePayments':
                    response = await this.backendService.testCronProcessInvoicePayments()
                    break
                case 'testCronCheckDueInvoices':
                    response = await this.backendService.testCronCheckDueInvoices()
                    break
                case 'testCronSendM2MRenewalReminders':
                    response = await this.backendService.testCronSendM2MRenewalReminders()
                    break
                case 'testCronUpdateMerchantResponseTime':
                    response = await this.backendService.testCronUpdateMerchantResponseTime()
                    break
                case 'schedulers-testCronSubscriptionExpired':
                    response = await this.backendService.testCronSubscriptionExpired()
                    break
                case 'schedulers-testCronRetryFirstPayments':
                    response = await this.backendService.testCronRetryFirstPayments()
                    break
                case 'testCloneClientsToAdminClients':
                    response = await this.backendService.testCloneClientsToAdminClients()
                    break
                case 'testCloneMerchantsToMerchantsPrivate':
                    response = await this.backendService.testCloneMerchantsToMerchantsPrivate()
                    break
                case 'testSaveDashboardData':
                    response = await this.backendService.testSaveDashboardData()
                    break
                case 'syncAdminClientsSubscriptionData':
                    response = await this.backendService.syncAdminClientsSubscriptionData()
                    break
                case 'testSyncGravatarAvatars':
                    response = await this.backendService.syncGravatarAvatars()
                    break
                case 'reconcileGoogleAdsConversions':
                    response = await this.backendService.reconcileGoogleAdsConversions()
                    break
                case 'verifyGoogleAdsConversions':
                    response = await this.backendService.verifyGoogleAdsConversions()
                    break
                case 'testUploadGoogleAdsConversions':
                    response = await this.backendService.uploadGoogleAdsConversions()
                    break
                case 'migrateCloseOldLeads':
                    response = await this.backendService.closeOldLeads()
                    break
                case 'deleteCrmUsers':
                    const uidsResult = await Swal.fire({
                        title: 'Delete CRM Users',
                        input: 'textarea',
                        inputLabel: 'User UIDs (one per line or comma-separated)',
                        inputPlaceholder: 'uid1\nuid2',
                        showCancelButton: true,
                        confirmButtonText: 'Delete',
                        confirmButtonColor: '#dc3545',
                        inputValidator: (value) => {
                            if (!value?.trim()) return 'At least one UID is required'
                            return null
                        },
                    })
                    if (!uidsResult.isConfirmed || !uidsResult.value) {
                        route.isRunning = false
                        return
                    }
                    const uids = String(uidsResult.value)
                        .split(/[\s,]+/)
                        .map((uid) => uid.trim())
                        .filter(Boolean)
                    response = await this.backendService.deleteCrmUsers(uids)
                    break
                case 'migrateOccupantsToParkers':
                    response = await this.backendService.migrateOccupantsToParkers()
                    break
                case 'reconcileAdmytAccess':
                    response = await this.backendService.reconcileAdmytAccess()
                    break
                case 'testSyncAllAdminClientsToOmnisend':
                    response = await this.backendService.syncAllAdminClientsToOmnisend()
                    break
                case 'seedOmnisendProductCategories':
                    response = await this.backendService.seedOmnisendProductCategories()
                    break
                case 'seedFaqs':
                    response = await this.backendService.seedFaqs()
                    break
                case 'repairEventLogTimestamps':
                    response = await this.backendService.repairEventLogTimestamps()
                    break
                case 'prospecting-testCronPollProspectScrapes':
                    response = await this.backendService.testCronPollProspectScrapes()
                    break
                case 'testSendMerchantMonthlyReport':
                    const merchantKeyResult = await Swal.fire({
                        title: 'Merchant Monthly Report',
                        input: 'text',
                        inputLabel: 'Merchant Key (leave blank to send for ALL merchants)',
                        inputPlaceholder: 'e.g. abc123... (optional)',
                        showCancelButton: true,
                        confirmButtonText: 'Send Report',
                    })
                    if (!merchantKeyResult.isConfirmed) {
                        route.isRunning = false
                        return
                    }
                    response = await this.backendService.sendMerchantMonthlyReport(merchantKeyResult.value || undefined)
                    break
                case 'testPreviewMerchantMonthlyReport':
                    const previewMerchantKeyResult = await Swal.fire({
                        title: 'Preview Merchant Monthly Report',
                        html: 'Reports will be sent to <strong>mark@, lesedi@, jett@, accounts@parkupp.co.za</strong> instead of the actual merchants.',
                        input: 'text',
                        inputLabel: 'Merchant Key (leave blank to preview ALL merchants)',
                        inputPlaceholder: 'e.g. abc123... (optional)',
                        showCancelButton: true,
                        confirmButtonText: 'Send Preview',
                    })
                    if (!previewMerchantKeyResult.isConfirmed) {
                        route.isRunning = false
                        return
                    }
                    response = await this.backendService.previewMerchantMonthlyReport(previewMerchantKeyResult.value || undefined)
                    break
                case 'resyncClientAdmyt':
                    const clientIdResult = await Swal.fire({
                        title: 'Enter Client ID',
                        input: 'text',
                        inputLabel: 'Firestore Client Document ID',
                        inputPlaceholder: 'e.g. abc123...',
                        showCancelButton: true,
                        inputValidator: (value) => {
                            if (!value) return 'Client ID is required'
                            return null
                        },
                    })
                    if (!clientIdResult.isConfirmed || !clientIdResult.value) {
                        route.isRunning = false
                        return
                    }
                    const resyncResult = await this.admytService.resyncClientAdmyt(clientIdResult.value)
                    response = resyncResult.data
                    break
                case 'refreshParkingInsights':
                    response = await this.backendService.refreshParkingInsights()
                    break
                case 'refreshGoogleReviews':
                    response = await this.backendService.refreshGoogleReviews()
                    break
                case 'affiliateAdminGenerateStatements':
                    response = await this.backendService.generateAffiliateStatements()
                    break
                case 'backfillClientAttribution-dry':
                    response = await this.backendService.backfillClientAttribution(false)
                    break
                case 'backfillClientAttribution-apply':
                    response = await this.backendService.backfillClientAttribution(true)
                    break
                case 'backfillParkingReports':
                    response = await this.backendService.backfillParkingReports()
                    break
                default:
                    throw new Error(`No backend service method for endpoint: ${route.endpoint}`)
            }

            await Swal.fire({
                title: 'Success!',
                html: `<strong>${route.name}</strong> executed successfully.<br><br><pre style="text-align: left; font-size: 12px;">${JSON.stringify(response, null, 2)}</pre>`,
                icon: 'success',
                confirmButtonText: 'OK',
            })
        } catch (error: any) {
            console.error(`Error running ${route.name}:`, error)
            console.error(`Error details:`, error.details)

            // Build detailed error message
            let errorHtml = `<strong>${route.name}</strong> failed.<br><br>`

            if (error.message) {
                errorHtml += `<strong>Message:</strong><br><pre style="text-align: left; font-size: 12px; margin-bottom: 10px;">${error.message}</pre>`
            }

            if (error.details) {
                errorHtml += `<strong>Details:</strong><br><pre style="text-align: left; font-size: 11px; max-height: 300px; overflow-y: auto;">${JSON.stringify(error.details, null, 2)}</pre>`
            } else if (error.code) {
                errorHtml += `<strong>Code:</strong> ${error.code}<br>`
            }

            await Swal.fire({
                title: 'Error',
                html: errorHtml,
                icon: 'error',
                confirmButtonText: 'OK',
                width: '700px',
            })
        } finally {
            route.isRunning = false
        }
    }

    getCategoryIcon(category: string): string {
        const icons: { [key: string]: string } = {
            CRM: 'fa-address-book',
            'Email Reports': 'fa-envelope',
            Invoices: 'fa-file-invoice',
            Subscriptions: 'fa-calendar-alt',
            'Data Sync': 'fa-sync',
            Admyt: 'fa-shield-alt',
            Omnisend: 'fa-envelope-open-text',
            Migrations: 'fa-database',
            CMS: 'fa-pen-to-square',
            Prospecting: 'fa-magnifying-glass-location',
            Affiliates: 'fa-handshake',
        }
        return icons[category] || 'fa-cog'
    }
}
